import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output, inject } from '@angular/core';
import { IrisWorkflowI } from '@iris/common/models/IrisWorkflow';
import { IrisTranslatePipe } from '@iris/common/modules/pipes-common/pipes';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import cytoscape from 'cytoscape';
import cola from 'cytoscape-cola';
import { IrisColorService } from '@iris/common/services/color.service';
import { SkModalBase, SkModalContentComponent, StrakitButtonPrefixDirective } from '@ngx-strakit/cdk';
import { TranslatePipe } from '@ngx-translate/core';
import { StrakitButtonComponent } from '@ngx-strakit/cdk/controls';

export interface IrisModuleWorkflowStateDiagramComponentData {
  workflow: IrisWorkflowI;
  canSave: boolean;
}

@Component({
  selector: 'module-workflow-state-diagram',
  templateUrl: './module-workflow-state-diagram.component.html',
  styleUrls: ['module-workflow-state-diagram.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SkModalContentComponent,
    TranslatePipe,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
})
export class IrisModuleWorkflowStateDiagramComponent extends SkModalBase<IrisModuleWorkflowStateDiagramComponent, IrisWorkflowI> implements AfterViewInit  {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly stateColors = inject(IrisColorService);
  readonly data = inject<IrisModuleWorkflowStateDiagramComponentData>(MAT_DIALOG_DATA);

  @Output() saveRequested = new EventEmitter<IrisWorkflowI>();

  readonly workflow = this.data.workflow;
  readonly canSave = this.data.canSave;
  irisTranslatePipe = new IrisTranslatePipe();
  readonly diagramHeight = 500;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cy: any;

  ngAfterViewInit(): void {
    const stateColors = {
      'START': {
        'OPEN': this.stateColors.colors['$grey300'],
      },
      'STEP': {
        'OPEN': this.stateColors.colors['$grey300'],
      },
      'END': {
        'RESOLVED': this.stateColors.colors['$primaryBlue'],
        'REJECTED': this.stateColors.colors['$error'],
        'OPEN': this.stateColors.colors['$grey300'],
      },
    };

    const nodes = [];
    const edges = [];
    const availableStateIds = this.workflow.workflowStates.map(state => state.id);
    this.workflow.workflowStates.forEach(state => {
      if (state.type == 'START') {
        nodes.push({
          data: {
            id: 'workflow_start_' + state.id,
          },
          classes: 'point-start',
        });
        edges.push({
          data: {
            source: 'workflow_start_' + state.id,
            target: state.id,
          },
          classes: 'straight',
        });
      }

      nodes.push({
        data: {
          id: state.id,
          bgColor: state.color || stateColors[state.type]['OPEN'],
        },
      });
      nodes.push({
        data: {
          id: state.id + '_type',
          parent: state.id,
          text: (state.resolution || 'Open').toLowerCase(),
          color: this.stateColors.colors['$white'],
          fontSize: '10',
        },
        classes: 'type',
      });
      nodes.push({
        data: {
          id: state.id + '_title',
          parent: state.id + '_type',
          text: state?.nameTranslated || state?.translated?.name || state?.name,
          color: this.stateColors.colors['$secondaryGrey500'],
          bgColor: this.stateColors.colors['$grey50Background'],
          size: 1,
          fontSize: '12',
        },
        classes: 'title',
      });

      if (state.type == 'END') {
        nodes.push({
          data: {
            id: 'workflow_end_' + state.id,
          },
          classes: 'point-end',
        });
        edges.push({
          data: {
            source: state.id,
            target: 'workflow_end_' + state.id,
          },
          classes: 'straight',
        });
      }

      state.toIds.forEach(toId => {
        if (availableStateIds.includes(toId)) {
          edges.push({
            data: {
              source: state.id,
              target: toId,
            },
          });
        }
      });
    });
    let layoutConf = {
      name: 'cola',
      nodeSpacing: () => 25,
    };

    if (Object.keys(this.workflow.settings || {}).length) {
      layoutConf = Object.assign(layoutConf, { name: 'preset' }, this.workflow.settings);
    }

    cytoscape.use(cola);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.cy = window.cy = cytoscape({
      container: document.getElementById('mycanvas'),
      layout: layoutConf,

      style: [
        {
          selector: 'node',
          style: {
            'min-width': '100',
            'shape': 'rectangle',
            'content': '',
            'background-color': 'data(bgColor)',
            'padding': '0',
            'border-width': '0',
            'overlay-opacity': 0,
          },
        },
        {
          selector: '.title',
          style: {
            'width': 'data(size)',
            'height': 'data(size)',
            'shape': 'ellipse',
            'content': 'data(text)',
            'font-size': 'data(fontSize)',
            'color': 'data(color)',
            'border-width': 0,
            'text-halign': 'center',
            'text-valign': 'center',
            'text-margin-x': 5,
            'text-margin-y': 2,
          },
        },
        {
          selector: '.type',
          style: {
            'min-width': '100',
            'shape': 'rectangle',
            'content': 'data(text)',
            'font-size': 'data(fontSize)',
            'color': 'data(color)',
            'border-width': '0',
            'padding': '5',
            'background-color': this.stateColors.colors['$grey50Background'],
            'z-index': 1,
            'text-halign': 'center',
            'text-valign': 'bottom',
            'text-background-padding': '1',
            'text-margin-y': 5,
          },
        },
        {
          selector: '.point-start',
          style: {
            'shape': 'ellipse',
            'width': '25',
            'height': '25',
            'border-width': '0',
            'background-color': this.stateColors.colors['$grey250Disabled'],
            'padding': '9',
          },
        },
        {
          selector: '.point-end',
          style: {
            'shape': 'ellipse',
            'width': '33',
            'height': '33',
            'border-width': '9',
            'border-color': this.stateColors.colors['$grey250Disabled'],
            'background-color': this.stateColors.colors['$secondaryGrey400'],
          },
        },
        {
          selector: 'edge',
          style: {
            'width': 1,
            'target-arrow-shape': 'triangle',
            'line-color': this.stateColors.colors['$grey250Disabled'],
            'source-arrow-color': this.stateColors.colors['$grey250Disabled'],
            'target-arrow-color': this.stateColors.colors['$grey250Disabled'],
            'curve-style': 'bezier',
          },
        },
        {
          selector: '.straight',
          style: {
            'curve-style': 'bezier',
          },
        },
      ],
      elements: {
        nodes,
        edges,
      },
    });
    this.cy.resize();
  }

  savePosition(): void {
    if (!this.canSave) { return; }
    this.changeDetector.markForCheck();
    const newSettings = {
      zoom: this.cy.zoom(),
      pan: this.cy.pan(),
      positions: {},
    };
    this.cy.nodes().forEach(n => {
      newSettings.positions[n.id()] = n.position();
    });
    this.saveRequested.emit({
      ...this.workflow,
      settings: newSettings,
    });
    this.closeWithoutResult();
  }

}
